import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Send } from 'lucide-react';

import { EmailPdfRequest } from '../services/legacyApi';
import { Button } from './ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from './ui/dialog';

interface SendPdfModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    documentLabel: string;
    documentNumber: string;
    attachmentName: string;
    defaultTo?: string;
    defaultCc?: string;
    defaultSubject: string;
    defaultBody: string;
    onSend: (payload: EmailPdfRequest) => Promise<any>;
    resolveDefaultTo?: () => Promise<string>;
}

const splitEmails = (value: string) =>
    value
        .replace(/;/g, ',')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);

const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

export function SendPdfModal({
    open,
    onOpenChange,
    documentLabel,
    documentNumber,
    attachmentName,
    defaultTo = '',
    defaultCc = '',
    defaultSubject,
    defaultBody,
    onSend,
    resolveDefaultTo,
}: SendPdfModalProps) {
    const [to, setTo] = useState(defaultTo);
    const [cc, setCc] = useState(defaultCc);
    const [subject, setSubject] = useState(defaultSubject);
    const [body, setBody] = useState(defaultBody);
    const [resolving, setResolving] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState<string>('');

    useEffect(() => {
        if (!open) return;
        setTo(defaultTo || '');
        setCc(defaultCc || '');
        setSubject(defaultSubject);
        setBody(defaultBody);
        setError('');
    }, [open, defaultTo, defaultCc, defaultSubject, defaultBody]);

    useEffect(() => {
        if (!open || defaultTo || !resolveDefaultTo) return;
        let cancelled = false;
        setResolving(true);
        resolveDefaultTo()
            .then((value) => {
                if (!cancelled) setTo(value || '');
            })
            .catch(() => {
                if (!cancelled) setTo('');
            })
            .finally(() => {
                if (!cancelled) setResolving(false);
            });
        return () => {
            cancelled = true;
        };
    }, [open, defaultTo, resolveDefaultTo]);

    const toError = useMemo(() => {
        const emails = splitEmails(to);
        if (emails.length === 0) return 'Recipient email is required.';
        if (emails.some((email) => !isValidEmail(email))) return 'Enter a valid recipient email address.';
        return '';
    }, [to]);

    const ccError = useMemo(() => {
        const emails = splitEmails(cc);
        if (emails.some((email) => !isValidEmail(email))) return 'Enter valid CC email addresses.';
        return '';
    }, [cc]);

    const handleSend = async () => {
        if (toError || ccError) {
            setError(toError || ccError);
            return;
        }
        try {
            setSending(true);
            setError('');
            await onSend({
                to: to.trim(),
                cc: cc.trim() || undefined,
                subject: subject.trim(),
                body,
            });
            onOpenChange(false);
        } catch (err: any) {
            setError(err?.message || 'Failed to send email');
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>Send {documentLabel}</DialogTitle>
                    <DialogDescription>
                        Email {documentNumber} as a PDF attachment. The current print format is used for the attached file.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="rounded-md border bg-slate-50 px-4 py-3 text-sm">
                        <div className="flex items-center justify-between gap-4">
                            <span className="text-slate-500">Attachment</span>
                            <span className="font-mono text-slate-800">{attachmentName}</span>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-700">To</label>
                        <input
                            type="email"
                            value={to}
                            onChange={(event) => setTo(event.target.value)}
                            placeholder={resolving ? 'Resolving default recipient...' : 'recipient@example.com'}
                            className="w-full rounded-md border px-3 py-2 text-sm"
                            disabled={sending || resolving}
                        />
                        {(toError || resolving) && (
                            <p className="text-xs text-slate-500">{resolving ? 'Loading default recipient...' : toError}</p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-700">CC</label>
                        <input
                            type="text"
                            value={cc}
                            onChange={(event) => setCc(event.target.value)}
                            placeholder="Optional CC addresses, separated by commas"
                            className="w-full rounded-md border px-3 py-2 text-sm"
                            disabled={sending}
                        />
                        {ccError && <p className="text-xs text-amber-600">{ccError}</p>}
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-700">Subject</label>
                        <input
                            type="text"
                            value={subject}
                            onChange={(event) => setSubject(event.target.value)}
                            className="w-full rounded-md border px-3 py-2 text-sm"
                            disabled={sending}
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-700">Body</label>
                        <textarea
                            value={body}
                            onChange={(event) => setBody(event.target.value)}
                            rows={8}
                            className="w-full rounded-md border px-3 py-2 text-sm"
                            disabled={sending}
                        />
                    </div>

                    {error && (
                        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                            {error}
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>
                        Cancel
                    </Button>
                    <Button onClick={handleSend} disabled={sending || resolving || !!toError || !!ccError}>
                        {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                        Send PDF
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
